/**
 * ss-search.js — Overlay open/close + owned predictive results (Shopify suggest JSON).
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function esc(str) {
    return String(str || '').replace(/[&<>"']/g, function (ch) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch];
    });
  }

  function formatPrice(product) {
    if (!product) return '';
    if (product.price && typeof product.price === 'string' && /[£$€]/.test(product.price)) return product.price;
    var cents = product.price_min != null ? product.price_min : product.price;
    var n = Number(cents);
    if (!isFinite(n)) return '';
    if (n >= 100 || String(cents).indexOf('.') === -1) n = n / 100;
    return '£' + n.toFixed(2);
  }

  function productImage(product) {
    if (!product) return '';
    if (product.image) return product.image;
    if (product.featured_image) {
      return typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image.url || product.featured_image.src || '';
    }
    return '';
  }

  function initSearch(details) {
    if (!details || details.dataset.ssSearchInit) return;
    details.dataset.ssSearchInit = '1';

    details.addEventListener('toggle', function () {
      var root = document.documentElement;
      if (details.open) {
        root.classList.add('ss-search-open', 'ss-header-above-search');
        document.body.classList.add('ss-search-open', 'overflow-hidden');
        var input = details.querySelector('.ss-search__input');
        if (input) {
          window.requestAnimationFrame(function () {
            setTimeout(function () {
              input.focus();
            }, 50);
          });
        }
      } else {
        root.classList.remove('ss-search-open', 'ss-header-above-search');
        document.body.classList.remove('ss-search-open', 'overflow-hidden');
        root.style.removeProperty('overflow');
        document.body.style.removeProperty('overflow');
        delete document.body.dataset.ssSearchScrollLocked;
      }
      if (typeof window.ssSyncScrollLock === 'function') window.ssSyncScrollLock();
    });

    var overlay = details.querySelector('[data-ss-search-overlay]');
    if (overlay) {
      overlay.addEventListener('click', function () {
        details.removeAttribute('open');
      });
    }

    var closeBtn = details.querySelector('.ss-search__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        details.removeAttribute('open');
      });
    }

    var input = details.querySelector('.ss-search__input');
    var clearBtn = details.querySelector('[data-ss-search-clear]');
    var results = details.querySelector('[data-ss-search-results]');
    var idle = details.querySelector('[data-ss-search-idle]');
    var statusEl = details.querySelector('[data-ss-search-status]');
    var cache = {};
    var suggestSeq = 0;
    var suggestAbort = null;

    function currentTerm() {
      return input ? String(input.value || '').trim() : '';
    }

    function syncClear() {
      if (!clearBtn || !input) return;
      if (input.value && input.value.length) clearBtn.classList.add('is-visible');
      else clearBtn.classList.remove('is-visible');
    }

    function setStatus(text) {
      if (!statusEl) return;
      statusEl.textContent = text || '';
    }

    function renderEmpty(term) {
      if (!results) return;
      results.hidden = false;
      results.innerHTML =
        '<p class="ss-search__empty">No products match “' +
        esc(term) +
        '”. Try another name or brand.</p>' +
        '<a class="ss-search__all" href="/search?q=' +
        encodeURIComponent(term) +
        '">Search the full catalogue</a>';
      if (idle) idle.hidden = true;
    }

    function renderProducts(term, products) {
      if (!results) return;
      term = currentTerm() || term;
      if (!products || !products.length) {
        renderEmpty(term);
        return;
      }
      var html = '<p class="ss-search__label">Products</p><ul class="ss-search__list" role="listbox">';
      products.slice(0, 6).forEach(function (p) {
        var href = p.url || ('/products/' + (p.handle || ''));
        var img = productImage(p);
        var price = formatPrice(p);
        html +=
          '<li role="option">' +
          '<a class="ss-search__hit" href="' +
          esc(href) +
          '">' +
          (img
            ? '<img class="ss-search__hit-img" src="' + esc(img) + '" alt="" width="56" height="56" loading="lazy">'
            : '<span class="ss-search__hit-ph" aria-hidden="true"></span>') +
          '<span class="ss-search__hit-copy">' +
          (p.vendor ? '<span class="ss-search__hit-vendor">' + esc(p.vendor) + '</span>' : '') +
          '<span class="ss-search__hit-title">' +
          esc(p.title) +
          '</span>' +
          (price ? '<span class="ss-search__hit-price">' + esc(price) + '</span>' : '') +
          '</span></a></li>';
      });
      html +=
        '</ul><a class="ss-search__all" href="/search?q=' +
        encodeURIComponent(term) +
        '">Search for “' +
        esc(term) +
        '”</a>';
      results.hidden = false;
      results.innerHTML = html;
      if (idle) idle.hidden = true;
      setStatus(products.length + ' products');
    }

    function fetchSuggest(term) {
      var key = term.toLowerCase();
      var seq = ++suggestSeq;
      if (cache[key]) {
        renderProducts(currentTerm() || term, cache[key]);
        return;
      }
      if (suggestAbort && typeof suggestAbort.abort === 'function') {
        try { suggestAbort.abort(); } catch (e) {}
      }
      suggestAbort = typeof AbortController === 'function' ? new AbortController() : null;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url =
        root +
        'search/suggest.json?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
      if (results) {
        results.hidden = false;
        results.innerHTML = '<p class="ss-search__loading"><span class="ss-search__spinner" aria-hidden="true"></span> Searching…</p>';
      }
      if (idle) idle.hidden = true;
      fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: suggestAbort ? suggestAbort.signal : undefined
      })
        .then(function (r) {
          if (!r.ok) throw new Error('suggest');
          return r.json();
        })
        .then(function (data) {
          if (seq !== suggestSeq) return;
          var products =
            (data && data.resources && data.resources.results && data.resources.results.products) || [];
          cache[key] = products;
          renderProducts(currentTerm() || term, products);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (seq !== suggestSeq) return;
          renderEmpty(currentTerm() || term);
        });
    }

    var onType = debounce(function () {
      if (!input) return;
      var term = input.value.trim();
      syncClear();
      if (!term) {
        if (results) {
          results.hidden = true;
          results.innerHTML = '';
        }
        if (idle) idle.hidden = false;
        setStatus('');
        return;
      }
      fetchSuggest(term);
    }, 280);

    if (input) {
      input.addEventListener('input', onType);
      input.addEventListener('change', syncClear);
    }
    if (clearBtn && input) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        syncClear();
        input.focus();
      });
    }
    syncClear();

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!details.open) return;
      details.removeAttribute('open');
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.ss-search details').forEach(initSearch);
  });

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('.ss-search details').forEach(initSearch);
  });
})();
